import { supabase } from "~/integrations/supabase/client";

export interface ProtestedCheckValidation {
  canCreateSalesRequest: boolean;
  reason?: string;
  protestedChecks?: Array<{
    id: number;
    check_number: string | null;
    amount: number;
    bank: string | null;
    collection_date: string | null;
  }>;
}

/**
 * Checks if a client has any protested checks that would prevent creating sales requests
 */
export const validateClientForProtestedChecks = async (
  clientId: string
): Promise<ProtestedCheckValidation> => {
  try {
    // Query for protested checks for this client
    const { data: protestedChecks, error } = await supabase
      .from("payments")
      .select(
        `
        id,
        check_number,
        amount,
        bank,
        collection_date
      `
      )
      .eq("client", clientId)
      .eq("payment_method", "check")
      .eq("check_status", "protested"); // English status value

    if (error) {
      console.error("Error checking for protested checks:", error);
      // In case of error, allow creation but log the error
      return { canCreateSalesRequest: true };
    }

    // If there are protested checks, block creation
    if (protestedChecks && protestedChecks.length > 0) {
      return {
        canCreateSalesRequest: false,
        reason: "protested_checks",
        protestedChecks,
      };
    }

    // No protested checks found, allow creation
    return { canCreateSalesRequest: true };
  } catch (error) {
    console.error("Error in validateClientForProtestedChecks:", error);
    // In case of unexpected error, allow creation but log the error
    return { canCreateSalesRequest: true };
  }
};

/**
 * Gets a formatted message for protested checks blocking
 */
export const getProtestedChecksBlockMessage = (
  protestedChecks: Array<{
    check_number: string | null;
    amount: number;
    bank: string | null;
  }>
): string => {
  if (protestedChecks.length === 1) {
    const check = protestedChecks[0];
    return `No se puede crear la nota de venta. El cliente tiene un cheque protestado (N° ${
      check.check_number || "N/A"
    } por $${check.amount.toLocaleString("es-CL")}). No se podrán realizar ventas hasta regularizar el pago.`;
  } else {
    const totalAmount = protestedChecks.reduce(
      (sum, check) => sum + check.amount,
      0
    );
    return `No se puede crear la nota de venta. El cliente tiene ${
      protestedChecks.length
    } cheques protestados por un total de $${totalAmount.toLocaleString(
      "es-CL"
    )}. No se podrán realizar ventas hasta regularizar todos los pagos.`;
  }
};
